import type { TranslatableLabel } from '@/types/i18n.types';

/** Nom des icônes associables aux messages d'urgence */
export enum UserBroadcastEmergencyIconMap {
  exclamation = 'exclamation',
  noInternet = 'noInternet',
}

/** Nom des icônes associables aux messages d'avertissement */
export enum UserBroadcastWarningIconMap {
  default = 'default',
}

/** Types de messages globaux */
export enum UserBroadcastImportanceGroup {
  emergencies = 'emergencies',
  warnings = 'warnings',
}

/**
 * Liste les IDs statiques des messages à afficher dans la zone d'urgence (Maintenant, hors ligne, etc.)
 * L'ordre des clés détermine aussi l'ordre d'importance lors du tri des messages.
 */
export enum GlobalMessageIds {
  maintenanceOngoing = 'maintenanceOngoing',
  networkIssue = 'networkIssue',
  offlineIssue = 'offlineIssue',
}

/**
 * Objet composant les messages provenant directement de l'admin et retourné par le backend,
 * qu'ils soit de type urgents ou des avertissements.
 */
export type UserBroadcastMessageBase = {
  id: string;
  content: TranslatableLabel;
  title: TranslatableLabel;
};

/** Messages d'avertissement provenant de l'admin */
export type UserBroadcastMessageWarning = UserBroadcastMessageBase & {
  icon: UserBroadcastWarningIconMap;
  title: TranslatableLabel;
};

/** Messages d'urgence provenant de l'admin, ou problème de réseau, etc. */
export type EmergencyMessage = UserBroadcastMessageBase & {
  icon: UserBroadcastEmergencyIconMap;
};

/** Message indiquant que le système est en maintenance */
export type MaintenanceMessage = Omit<UserBroadcastMessageBase, 'title'> & {
  icon: UserBroadcastEmergencyIconMap.exclamation;
  // titre laissé optionnel de façon volontaire pour éviter de devoir changer trop de code existant
  title?: TranslatableLabel;
};

/** Messages `bon à savoir` provenant de l'admin */
export type UserBroadcastMessageTips = UserBroadcastMessageBase & {
  startDate: string;
};

/** Les différents types de messages d'urgences pouvant être gérés côté Frontend */
export type UserBroadcastMessageEmergency = EmergencyMessage | MaintenanceMessage;

/**
 * Messages adaptés depuis les données backend et incluant au besoin ceux générés côté frontend (exemple: messages de
 * maintenance, problèmes réseau, etc.).
 */
export type SystemMessages = {
  [UserBroadcastImportanceGroup.emergencies]: UserBroadcastMessageEmergency[];
  [UserBroadcastImportanceGroup.warnings]: UserBroadcastMessageWarning[];
};
